<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useCVSectionTranslations } from '~/composables/useCVSectionTranslations';
import { isRTLLanguage } from '~/constants/cvLanguages';
import { getLinkDisplayName, getLinkMeta } from '~/constants/linkTypes';
import type { CVData, Education as OldEducation, Experience as OldExperience } from '~/types';
import type { Education as CvEducation, Experience as CvExperience } from '~/types/cv';
import { formatDateOfBirth } from '~/utils/dateHelpers';

// Local types for timeline sections
type TimelineExperience = {
  company?: string;
  title?: string;
  startDate?: string;
  endDate?: string;
  period?: string;
  description?: string;
}

type TimelineEducation = {
  school: string;
  degree?: string;
  startDate?: string;
  endDate?: string;
  period?: string;
}

const props = defineProps<{
  cvData: CVData;
  experiencesList: Array<TimelineExperience | CvExperience | OldExperience>;
  educationList: Array<TimelineEducation | CvEducation | OldEducation>;
  showQRCode?: boolean;
}>();

const { t } = useI18n()

// Get CV section translations based on cvLanguage
const cvLanguage = computed(() => props.cvData?.cvLanguage || 'cs')
const { translations: sectionTranslations } = useCVSectionTranslations(cvLanguage)
const isRTL = computed(() => isRTLLanguage(cvLanguage.value))

// Levels, dates and the word for "present" read the way the PDF reads them
const { skillLevel, languageLevel, languagePercent, formatPeriod, formatCertificateDate } = useWebCv(
  () => props.cvData,
  () => ({ present: sectionTranslations.value.present || t('cv.edit.present'), nativeSpeaker: sectionTranslations.value.nativeSpeaker }),
)

/** The CV's links (GitHub, portfolio…), shown as chips under the hero buttons. */
const websites = computed(() => (props.cvData?.websites ?? []).filter(site => site?.url))

const certificates = computed(() => (props.cvData?.certificates ?? []).filter(cert => cert && (cert.name || cert.issuer)))
const hobbies = computed(() => (props.cvData?.hobbies ?? []).filter(hobby => hobby && hobby.trim()))

/** Location, date of birth and driving licence shown as chips under the hero title. */
const personalDetails = computed(() => {
  const contact = props.cvData?.contact
  return [
    { key: 'location', icon: 'bi bi-geo-alt-fill', label: sectionTranslations.value.location, value: contact?.location ?? '' },
    { key: 'dateOfBirth', icon: 'bi bi-calendar3', label: sectionTranslations.value.dateOfBirth, value: formatDateOfBirth(contact?.dateOfBirth, cvLanguage.value) },
    { key: 'drivingLicense', icon: 'bi bi-car-front-fill', label: sectionTranslations.value.drivingLicense, value: props.cvData?.drivingLicense ?? '' },
  ].filter(item => item.value && item.value.trim())
})

// Computed properties
const { publicCvLinkPath } = usePublicCvLinks();
const contactInfo = computed<{ email?: string; phone?: string; website: string }>(() => {
  const contact = (props.cvData?.contact ?? {}) as Partial<NonNullable<CVData['contact']>>;
  const route = useRoute();
  const path = publicCvLinkPath('card', String(route.params.id));
  const publicCardUrl = import.meta.client ? `${window.location.origin}${path}` : path;

  return {
    email: contact.email,
    phone: contact.phone,
    website: publicCardUrl,
  };
});

const getExperienceRole = (exp: TimelineExperience | CvExperience | OldExperience): string => exp?.title ?? exp?.position ?? ''

// Animation states
const isVisible = ref(false);

onMounted(() => {
  setTimeout(() => {
    isVisible.value = true;
  }, 100);
});
</script>

<template>
  <div
v-if="props.cvData" :dir="isRTL ? 'rtl' : 'ltr'"
    class="relative min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-gray-900 dark:to-gray-800">
    <!-- Premium Flat Design Business Web Template -->

    <!-- Hero Section with Floating Navigation -->
    <div class="relative overflow-hidden">
      <!-- Background Pattern -->
      <div class="absolute inset-0 opacity-5 dark:opacity-10">
        <div class="absolute left-32 top-20 size-72 rounded-full bg-indigo-600 blur-3xl" />
        <div class="absolute -right-4 top-40 size-72 rounded-full bg-indigo-600 blur-3xl" />
      </div>

      <!-- Hero Content -->
      <div class="relative mx-auto max-w-7xl px-4 pb-2 pt-24 sm:pb-10 sm:pt-20">
        <div class="grid items-start gap-8 sm:gap-12 lg:grid-cols-3" :class="{ 'flex-row-reverse': isRTL }">
          <!-- Left: Text Content - Takes 2 columns -->
          <div
:class="{ 'animate-slideInLeft': isVisible }"
            class="order-last space-y-6 text-center sm:space-y-8 lg:order-first lg:col-span-2 lg:text-left">
            <!-- Badge -->
            <div
v-if="false"
              class="inline-flex items-center space-x-2 rounded-full bg-indigo-50 px-3 py-1.5 sm:px-4 sm:py-2 dark:bg-indigo-900/30">
              <div class="size-2 animate-pulse rounded-full bg-indigo-600 dark:bg-indigo-400" />
              <div class="text-xs font-medium text-indigo-600 sm:text-sm dark:text-indigo-400"/>
            </div>

            <!-- Main Heading -->
            <div class="mt-10" :class="{ 'text-right': isRTL }">
              <h1
                class="text-3xl font-bold leading-tight text-gray-900 sm:text-4xl lg:text-5xl xl:text-6xl dark:text-white" :class="{ 'text-right': isRTL }">
                {{ sectionTranslations.hiIm }} <div class="inline text-indigo-600 dark:text-indigo-400">{{
                  props.cvData?.name?.split(' ')[0] }}
                </div>
              </h1>
              <div class="mt-3 text-lg text-gray-600 sm:mt-4 sm:text-xl lg:text-2xl dark:text-gray-300" :class="{ 'text-right': isRTL }">
                {{ props.cvData?.title || '' }}
              </div>

              <!-- Personal details: location, date of birth, driving licence -->
              <div v-if="personalDetails.length" class="mt-4 flex flex-wrap gap-2" :class="{ 'justify-end': isRTL }">
                <span
                  v-for="item in personalDetails" :key="item.key"
                  class="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  <i :class="item.icon" class="text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
                  <span class="sr-only">{{ item.label }}:</span>
                  {{ item.value }}
                </span>
              </div>
            </div>

            <!-- About Preview -->
            <div class="text-sm leading-relaxed text-gray-600 sm:text-base lg:text-lg dark:text-gray-300" :class="{ 'text-right': isRTL }">
              {{ props.cvData?.aboutMe?.substring(0, 150) }}{{ props.cvData?.aboutMe?.length > 150 ? '...' : '' }}
            </div>

            <!-- Quick Actions -->
            <div class="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4 lg:justify-start">
              <a
v-if="contactInfo?.email" :href="`mailto:${contactInfo.email}`"
                class="inline-flex items-center justify-center space-x-2 rounded-full border-2 border-indigo-600 px-5 py-2.5 text-sm text-indigo-500 transition-all hover:bg-indigo-600 hover:text-white sm:px-6 sm:py-3 sm:text-base">
                <svg class="size-4 sm:size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div class="font-medium" :class="{ 'pr-3': isRTL }">{{ sectionTranslations.getInTouch }}</div>
              </a>
              <a
v-if="props.cvData?.contact?.website" :href="props.cvData.contact.website" target="_blank"
                class="inline-flex items-center justify-center space-x-2 rounded-full bg-gray-900 px-5 py-2.5 text-sm text-white transition-all hover:bg-gray-800 sm:px-6 sm:py-3 sm:text-base">
                <svg class="size-4 sm:size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <div class="font-medium" :class="{ 'pr-3': isRTL }">{{ sectionTranslations.websites }}</div>
              </a>
            </div>

            <!-- Links from the CV -->
            <ul v-if="websites.length" class="flex flex-wrap justify-center gap-2 lg:justify-start" :class="{ 'lg:justify-end': isRTL }">
              <li v-for="site in websites" :key="site.url">
                <a
                  :href="site.url" target="_blank" rel="noopener noreferrer"
                  class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-indigo-300 hover:text-indigo-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-indigo-400">
                  <i :class="getLinkMeta(site).biClass" aria-hidden="true" />{{ getLinkDisplayName(site) }}
                </a>
              </li>
            </ul>
          </div>

          <!-- Right: Photo - Smaller, takes 1 column -->
          <div :class="{ 'animate-slideInRight': isVisible }" class="relative order-first lg:order-last lg:col-span-1">
            <!-- Profile Image Container -->
            <div class="relative mx-auto w-full max-w-xs">
              <!-- Decorative Elements -->
              <div
                class="absolute -inset-4 rounded-3xl bg-gradient-to-r from-indigo-100 to-indigo-50 opacity-70 blur-2xl dark:from-indigo-900/20 dark:to-indigo-800/10" />

              <!-- Main Image -->
              <div class="relative">
                <div
v-if="props.cvData?.photoUrl"
                  class="relative overflow-hidden rounded-2xl shadow-xl sm:rounded-3xl sm:shadow-2xl">
                  <img
:src="props.cvData.photoUrl" :alt="props.cvData?.name"
                    class="h-[280px] w-full object-cover sm:h-[320px] ">
                  <!-- Overlay Gradient -->
                  <div class="absolute inset-0 bg-gradient-to-t from-gray-900/20 to-transparent" />
                </div>
                <div
v-else
                  class="relative flex h-[280px] items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-700 sm:h-[320px] sm:rounded-3xl">
                  <div class="text-6xl font-bold text-white/20 sm:text-7xl">{{ props.cvData?.name?.charAt(0)
                    || 'A' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Stats Cards Row - Below Hero Content -->
        <div class="relative mt-8 sm:mt-12">
          <div class="mx-auto flex max-w-4xl flex-wrap justify-center gap-3 sm:gap-4">
            <!-- Experience Card -->
            <div
              class="group flex items-center space-x-3 rounded-xl bg-white px-4 py-3 shadow-md transition-all hover:shadow-lg sm:px-5 sm:py-3.5 dark:bg-gray-800">
              <div
                class="flex size-10 items-center justify-center rounded-lg bg-indigo-100 transition-colors group-hover:bg-indigo-200 dark:bg-indigo-900/50 dark:group-hover:bg-indigo-900/70"
                :class="{ 'ml-3': isRTL }">
                <svg
class="size-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <div class="text-lg font-bold text-gray-900 sm:text-xl dark:text-white">{{ props.experiencesList?.length
                  || 0 }}+</div>
                <div class="text-xs text-gray-600 dark:text-gray-400">{{ sectionTranslations.experiences }}</div>
              </div>
            </div>

            <!-- Skills Card -->
            <div
              class="group flex items-center space-x-3 rounded-xl bg-white px-4 py-3 shadow-md transition-all hover:shadow-lg sm:px-5 sm:py-3.5 dark:bg-gray-800">
              <div
                class="flex size-10 items-center justify-center rounded-lg bg-green-100 transition-colors group-hover:bg-green-200 dark:bg-green-900/50 dark:group-hover:bg-green-900/70"
                :class="{ 'ml-3': isRTL }">
                <svg
class="size-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div class="text-lg font-bold text-gray-900 sm:text-xl dark:text-white">{{
                  (props.cvData?.technicalSkills?.length || 0) + (props.cvData?.softSkills?.length || 0) }}</div>
                <div class="text-xs text-gray-600 dark:text-gray-400">{{ sectionTranslations.skills }}</div>
              </div>
            </div>

            <!-- Languages Card -->
            <div
              class="group flex items-center space-x-3 rounded-xl bg-white px-4 py-3 shadow-md transition-all hover:shadow-lg sm:px-5 sm:py-3.5 dark:bg-gray-800">
              <div
                class="flex size-10 items-center justify-center rounded-lg bg-blue-100 transition-colors group-hover:bg-blue-200 dark:bg-blue-900/50 dark:group-hover:bg-blue-900/70"
                :class="{ 'ml-3': isRTL }">
                <svg
class="size-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
              </div>
              <div>
                <div class="text-lg font-bold text-gray-900 sm:text-xl dark:text-white">{{
                  props.cvData?.languages?.length || 0 }}</div>
                <div class="text-xs text-gray-600 dark:text-gray-400">{{ sectionTranslations.languages }}</div>
              </div>
            </div>

            <!-- Education Card -->
            <div
              class="group flex items-center space-x-3 rounded-xl bg-white px-4 py-3 shadow-md transition-all hover:shadow-lg sm:px-5 sm:py-3.5 dark:bg-gray-800">
              <div
                class="flex size-10 items-center justify-center rounded-lg bg-amber-100 transition-colors group-hover:bg-amber-200 dark:bg-amber-900/50 dark:group-hover:bg-amber-900/70"
                :class="{ 'ml-3': isRTL }">
                <svg
class="size-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                </svg>
              </div>
              <div>
                <div class="text-lg font-bold text-gray-900 sm:text-xl dark:text-white">{{ props.educationList?.length
                  || 0 }}</div>
                <div class="text-xs text-gray-600 dark:text-gray-400">{{ sectionTranslations.degrees }}</div>
              </div>
            </div>

            <!-- Contact Channels Card -->
            <div
              class="group flex items-center space-x-3 rounded-xl bg-white px-4 py-3 shadow-md transition-all hover:shadow-lg sm:px-5 sm:py-3.5 dark:bg-gray-800">
              <div
                class="flex size-10 items-center justify-center rounded-lg bg-purple-100 transition-colors group-hover:bg-purple-200 dark:bg-purple-900/50 dark:group-hover:bg-purple-900/70"
                :class="{ 'ml-3': isRTL }">
                <svg
class="size-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <div class="text-lg font-bold text-gray-900 sm:text-xl dark:text-white">{{ [contactInfo?.email,
                  contactInfo?.phone, props.cvData?.contact?.website, props.cvData?.contact?.linkedin].filter(Boolean).length }}</div>
                <div class="text-xs text-gray-600 dark:text-gray-400">{{ sectionTranslations.channels }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- About Section -->
    <section v-if="props.cvData?.aboutMe" id="about" class="bg-white py-12 sm:py-20 dark:bg-gray-900">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 gap-8 sm:gap-12 lg:grid-cols-3">
          <!-- Section Title -->
          <div class="text-center lg:col-span-1 lg:text-left">
            <h2 class="text-2xl font-bold text-gray-900 sm:text-3xl dark:text-white">{{ sectionTranslations.aboutMe }}
            </h2>
            <div class="mx-auto mt-3 h-1 w-20 rounded-full bg-indigo-600 sm:mt-4 lg:mx-0 dark:bg-indigo-400" />
          </div>

          <!-- Content -->
          <div class="space-y-6 lg:col-span-2">
            <div class="whitespace-pre-line text-base leading-relaxed text-gray-600 sm:text-lg dark:text-gray-300">{{ props.cvData?.aboutMe }}</div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="props.cvData?.languages?.length || props.educationList?.length" id="languages" class="bg-white py-12 sm:py-20 dark:bg-gray-900">
      <div class="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 sm:px-6 md:grid-cols-2 lg:px-8">
        <div v-if="props.cvData?.languages?.length" class="space-y-3 md:pr-8">
          <h3 class="flex items-center space-x-2 font-semibold text-gray-900 dark:text-white">
            <svg
class="size-5 text-indigo-600 dark:text-indigo-400" :class="{ 'ml-3': isRTL }" fill="none" stroke="currentColor"
              viewBox="0 0 24 24">
              <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
            </svg>
            <div>{{ sectionTranslations.languages }}</div>
          </h3>
          <div class="space-y-3">
            <div v-for="lang in props.cvData.languages" :key="lang.name">
              <div class="flex items-center justify-between">
                <div class="text-gray-600 dark:text-gray-400">{{ lang.name }}</div>
                <div v-if="lang.level" class="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {{ languageLevel(lang.level) }}</div>
              </div>
              <!-- With levels on, and only for a level we can read -->
              <div v-if="languagePercent(lang.level) > 0" class="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800" aria-hidden="true">
                <div class="h-full rounded-full bg-indigo-600 dark:bg-indigo-400" :style="{ width: `${languagePercent(lang.level)}%` }" />
              </div>
            </div>
          </div>
        </div>

        <div v-if="props.educationList?.length" class="space-y-3">
          <h3 class="flex items-center space-x-2 font-semibold text-gray-900 dark:text-white">
            <svg
class="size-5 text-indigo-600 dark:text-indigo-400" :class="{ 'ml-3': isRTL }" fill="none" stroke="currentColor"
              viewBox="0 0 24 24">
              <path d="M12 14l9-5-9-5-9 5 9 5z" />
              <path
                d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
            </svg>
            <div>{{ sectionTranslations.education }}</div>
          </h3>
          <div class="space-y-2">
            <div
v-for="(edu, index) in props.educationList" :key="index"
              class="flex items-center justify-between">
              <div class="flex flex-col">
                <div class="font-medium text-gray-900 dark:text-white">{{ edu.degree }}</div>
                <div class="text-sm text-gray-600 dark:text-gray-400">{{ edu.school }}</div>
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-400">{{ formatPeriod(edu) }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Experience Timeline -->
    <section v-if="props.experiencesList?.length" id="experience" class="bg-gray-50 py-12 sm:py-20 dark:bg-gray-800">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="mb-8 text-center sm:mb-12">
          <h2 class="text-2xl font-bold text-gray-900 sm:text-3xl dark:text-white">{{
            sectionTranslations.professionalJourney }}</h2>
          <div class="mt-3 text-base text-gray-600 sm:mt-4 sm:text-lg dark:text-gray-300">{{
            sectionTranslations.careerHighlights }}</div>
        </div>

        <!-- Mobile Timeline -->
        <div class="block lg:hidden">
          <div class="space-y-6">
            <div v-for="(exp, index) in props.experiencesList" :key="index" class="relative pl-8">
              <!-- Timeline Line -->
              <div class="absolute bottom-0 left-0 top-8 w-0.5 bg-gray-300 dark:bg-gray-600" />
              <!-- Timeline Dot -->
              <div
                class="absolute left-0 top-2 size-4 -translate-x-[7px] rounded-full border-4 border-white bg-indigo-600 dark:border-gray-800 dark:bg-indigo-400" />

              <!-- Content Card -->
              <div class="rounded-xl bg-white p-4 shadow-lg dark:bg-gray-900">
                <!-- Date Badge -->
                <div
                  class="mb-3 inline-block rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                  {{ formatPeriod(exp) }}
                </div>
                <!-- Job Details -->
                <h3 class="mb-1 text-lg font-bold text-gray-900 dark:text-white">
                  {{ getExperienceRole(exp) }}
                </h3>
                <div class="mb-2 text-sm font-medium text-indigo-600 dark:text-indigo-400">
                  {{ exp.company }}
                </div>
                <div v-if="exp.description" class="whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-gray-300">{{ exp.description }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Timeline -->
        <div class="relative hidden lg:block">
          <!-- Vertical Line -->
          <div class="absolute left-1/2 h-full w-0.5 -translate-x-1/2 bg-gray-300 dark:bg-gray-600" />

          <!-- Experience Items -->
          <div class="space-y-12">
            <div
v-for="(exp, index) in props.experiencesList" :key="index"
              :class="index % 2 === 0 ? 'flex justify-end' : 'flex justify-start'" class="relative">

              <!-- Timeline Dot -->
              <div
                class="absolute left-1/2 size-4 -translate-x-1/2 rounded-full border-4 border-white bg-indigo-600 dark:border-gray-800 dark:bg-indigo-400" />

              <!-- Content Card -->
              <div :class="index % 2 === 0 ? 'mr-8' : 'ml-8'" class="w-5/12">
                <div class="rounded-2xl bg-white p-6 shadow-lg transition-shadow hover:shadow-xl dark:bg-gray-900">
                  <!-- Date Badge -->
                  <div
                    class="mb-4 inline-block rounded-full bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                    {{ formatPeriod(exp) }}
                  </div>

                  <!-- Job Details -->
                  <h3 class="mb-2 text-xl font-bold text-gray-900 dark:text-white">
                    {{ getExperienceRole(exp) }}
                  </h3>
                  <div class="mb-3 font-medium text-indigo-600 dark:text-indigo-400">
                    {{ exp.company }}
                  </div>
                  <div v-if="exp.description" class="whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-gray-300">{{ exp.description }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Skills Grid -->
    <section v-if="props.cvData?.technicalSkills?.length || props.cvData?.softSkills?.length" id="skills" class="bg-white py-12 sm:py-20 dark:bg-gray-900">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="mb-8 text-center sm:mb-12">
          <h2 class="text-2xl font-bold text-gray-900 sm:text-3xl dark:text-white">{{ sectionTranslations.skills }}</h2>
          <div class="mt-3 text-base text-gray-600 sm:mt-4 sm:text-lg dark:text-gray-300">{{
            sectionTranslations.technologiesDescription }}</div>
        </div>

        <div class="grid grid-cols-1 gap-8 sm:gap-12 md:grid-cols-2">
          <!-- Technical Skills -->
          <div v-if="props.cvData?.technicalSkills?.length">
            <div class="mb-4 flex items-center space-x-3 sm:mb-6">
              <div
                class="flex size-8 items-center justify-center rounded-lg bg-indigo-100 sm:size-10 sm:rounded-xl dark:bg-indigo-900/50">
                <svg
class="size-4 text-indigo-600 sm:size-5 dark:text-indigo-400" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <div class="text-lg font-bold text-gray-900 sm:text-xl dark:text-white">{{
                sectionTranslations.technicalSkills }}</div>
            </div>
            <div class="flex flex-wrap gap-2 sm:gap-3">
              <div
v-for="skill in props.cvData.technicalSkills" :key="skill"
                class="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-100 sm:px-4 sm:py-2 sm:text-base dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50">
                {{ skill }}<CVLevelDots :level="skillLevel(skill)" class="!text-indigo-600 dark:!text-indigo-300" />
              </div>
            </div>
          </div>

          <!-- Soft Skills -->
          <div v-if="props.cvData?.softSkills?.length">
            <div class="mb-4 flex items-center space-x-3 sm:mb-6">
              <div
                class="flex size-8 items-center justify-center rounded-lg bg-green-100 sm:size-10 sm:rounded-xl dark:bg-green-900/50">
                <svg
class="size-4 text-green-600 sm:size-5 dark:text-green-400" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div class="text-lg font-bold text-gray-900 sm:text-xl dark:text-white">{{ sectionTranslations.softSkills
                }}</div>
            </div>
            <div class="flex flex-wrap gap-2 sm:gap-3">
              <div
v-for="skill in props.cvData.softSkills" :key="skill"
                class="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 transition-colors hover:bg-green-100 sm:px-4 sm:py-2 sm:text-base dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50">
                {{ skill }}<CVLevelDots :level="skillLevel(skill)" class="!text-green-600 dark:!text-green-300" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Custom Sections -->
    <section
      v-for="(section, sectionIndex) in props.cvData?.customSections"
      :key="'custom-' + sectionIndex"
      v-show="section.sectionTitle && section.items?.length"
      :id="'custom-section-' + sectionIndex"
      class="bg-gray-50 py-12 sm:py-20 dark:bg-gray-800"
    >
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="mb-8 text-center sm:mb-12">
          <h2 class="text-2xl font-bold text-gray-900 sm:text-3xl dark:text-white">
            {{ section.sectionTitle }}
          </h2>
          <div class="mx-auto mt-3 h-1 w-20 rounded-full bg-orange-500 sm:mt-4" />
        </div>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="(item, index) in section.items"
            :key="index"
            class="group overflow-hidden rounded-2xl bg-white shadow-lg transition-all hover:shadow-xl dark:bg-gray-900"
          >
            <!-- Item Image -->
            <div v-if="item.image" class="aspect-video overflow-hidden">
              <img
                :src="item.image"
                :alt="item.title"
                class="size-full object-cover transition-transform group-hover:scale-105"
              >
            </div>

            <!-- Item Content -->
            <div class="p-6">
              <a
                v-if="item.url"
                :href="item.url"
                target="_blank"
                rel="noopener noreferrer"
                class="mb-2 block text-lg font-bold text-indigo-600 hover:underline dark:text-indigo-400"
                :class="{ 'text-right': isRTL }"
              >
                {{ item.title }}
              </a>
              <h3
                v-else
                class="mb-2 text-lg font-bold text-gray-900 dark:text-white"
                :class="{ 'text-right': isRTL }"
              >
                {{ item.title }}
              </h3>
              <p
                v-if="item.text"
                class="whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-gray-300"
                :class="{ 'text-right': isRTL }"
              >
                {{ item.text }}
              </p>
              <p
                v-if="item.url"
                class="mt-2 break-all text-xs text-gray-500"
                :class="{ 'text-right': isRTL }"
              >
                {{ item.url }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Certificates & Interests -->
    <section v-if="certificates.length || hobbies.length" id="certificates" class="bg-white py-12 sm:py-20 dark:bg-gray-900">
      <div class="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div v-if="certificates.length">
          <h2 class="mb-6 text-2xl font-bold text-gray-900 sm:text-3xl dark:text-white" :class="{ 'text-right': isRTL }">
            {{ sectionTranslations.certificates }}
          </h2>
          <div class="space-y-3">
            <div
              v-for="(cert, index) in certificates"
              :key="index"
              class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
              :class="{ 'text-right': isRTL }"
            >
              <a
                v-if="cert.url"
                :href="cert.url"
                target="_blank"
                rel="noopener noreferrer"
                class="font-semibold text-indigo-600 hover:underline dark:text-indigo-400"
              >{{ cert.name }}</a>
              <div v-else class="font-semibold text-gray-900 dark:text-white">{{ cert.name }}</div>
              <div v-if="cert.issuer || cert.date" class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {{ [cert.issuer, formatCertificateDate(cert.date)].filter(Boolean).join(' · ') }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="hobbies.length">
          <h2 class="mb-6 text-2xl font-bold text-gray-900 sm:text-3xl dark:text-white" :class="{ 'text-right': isRTL }">
            {{ sectionTranslations.hobbies }}
          </h2>
          <div class="flex flex-wrap gap-2" :class="{ 'justify-end': isRTL }">
            <span
              v-for="(hobby, index) in hobbies"
              :key="index"
              class="rounded-full border border-indigo-200 bg-indigo-50 px-4 py-1.5 text-sm font-medium text-indigo-700 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300"
            >{{ hobby }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" class="bg-gradient-to-br from-indigo-600 to-indigo-700 py-12 sm:py-20">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="mb-8 text-center text-white sm:mb-12">
          <h2 class="text-2xl font-bold !text-indigo-100 sm:text-3xl">{{ sectionTranslations.letsConnect }}</h2>
          <div class="mt-3 text-base text-indigo-100 sm:mt-4 sm:text-lg">{{ sectionTranslations.openToOpportunities }}
          </div>
        </div>

        <div class="mx-auto grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 md:grid-cols-3 lg:gap-8">
          <!-- Email Card -->
          <div v-if="contactInfo?.email" class="group">
            <a
:href="`mailto:${contactInfo.email}`"
              class="block rounded-2xl bg-white/10 p-6 text-center backdrop-blur-lg transition-all hover:scale-105 hover:bg-white/20">
              <div class="mx-auto mb-4 flex size-12 items-center justify-center rounded-xl bg-white/20">
                <svg class="size-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 class="mb-2 font-semibold !text-white">{{ sectionTranslations.email }}</h3>
              <div class="text-sm text-indigo-100">{{ contactInfo.email }}</div>
            </a>
          </div>

          <!-- Phone Card -->
          <div v-if="contactInfo?.phone" class="group">
            <a
:href="`tel:${contactInfo.phone}`"
              class="block rounded-2xl bg-white/10 p-6 text-center backdrop-blur-lg transition-all hover:scale-105 hover:bg-white/20">
              <div class="mx-auto mb-4 flex size-12 items-center justify-center rounded-xl bg-white/20">
                <svg class="size-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 class="mb-2 font-semibold !text-white">{{ sectionTranslations.phone }}</h3>
              <div class="text-sm text-indigo-100">{{ contactInfo.phone }}</div>
            </a>
          </div>

          <!-- LinkedIn Card -->
          <div v-if="props.cvData?.contact?.linkedin" class="group">
            <a
:href="props.cvData.contact.linkedin" target="_blank"
              class="block rounded-2xl bg-white/10 p-6 text-center backdrop-blur-lg transition-all hover:scale-105 hover:bg-white/20">
              <div class="mx-auto mb-4 flex size-12 items-center justify-center rounded-xl bg-white/20">
                <svg class="size-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </div>
              <h3 class="mb-2 font-semibold !text-white">LinkedIn</h3>
              <div class="text-sm text-indigo-100">{{ sectionTranslations.connectWithMe }}</div>
            </a>
          </div>
        </div>

        <!-- QR Code Section -->
        <div v-if="contactInfo?.website && showQRCode" class="mt-8 text-center sm:mt-12">
          <div class="inline-block rounded-xl bg-white p-4 shadow-xl sm:rounded-2xl sm:p-6">
            <div class="mb-3 text-xs font-medium text-gray-600 sm:mb-4 sm:text-sm">{{
              sectionTranslations.scanToSaveContact }}</div>
            <CommonQRCode
:link="contactInfo.website" :size="150" background-color="#FFFFFF" foreground-color="#4F46E5"
              class="sm:hidden" />
            <CommonQRCode
:link="contactInfo.website" :size="200" background-color="#FFFFFF" foreground-color="#4F46E5"
              class="hidden sm:block" />
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-gray-700 py-8 dark:bg-gray-900">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <div class="text-gray-400 dark:text-gray-500">
            {{ sectionTranslations.createdWith }} <a
href="https://www.inmyresume.com" target="_blank"
              class="text-indigo-400 hover:text-indigo-300"><img
src="/logo_white.svg" alt="InMyResume"
                class="inline-block w-52 opacity-70"></a>
          </div>
        </div>
      </div>
    </footer>
  </div>

  <!-- No Data State -->
  <div v-else class="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div class="text-center">
      <div class="mx-auto mb-4 flex size-20 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-800">
        <svg class="size-10 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 class="mb-2 text-xl font-bold text-gray-900 dark:text-white">{{ sectionTranslations.noDataAvailable }}</h3>
      <div class="text-gray-600 dark:text-gray-400">{{ sectionTranslations.noDataDescription }}</div>
    </div>
  </div>
</template>

<style scoped>
/* Smooth animations */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slideInLeft {
  animation: slideInLeft 0.8s ease-out forwards;
}

.animate-slideInRight {
  animation: slideInRight 0.8s ease-out forwards;
}

/* Smooth scroll behavior */
html {
  scroll-behavior: smooth;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #4F46E5;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #4338CA;
}

/* Backdrop blur support */
@supports (backdrop-filter: blur(12px)) {
  .backdrop-blur-lg {
    backdrop-filter: blur(12px);
  }
}

/* Print styles */
@media print {
  nav, footer {
    display: none;
  }

  section {
    page-break-inside: avoid;
  }
}
</style>
